import { Alert, Button, Field, Modal } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FormEvent, type JSX } from 'react';
import { type Resolver, useForm } from 'react-hook-form';

import {
  DEFAULT_MENU_POSITION_FORM_VALUES,
  type MenuPositionFormFields,
  type MenuPositionFormValues,
  menuPositionFormSchema,
} from '../../../menus/category-form-schema';
import type { ServiceType } from '../../../menus/api';

export interface MenuPositionFormProps {
  title: string;
  submitLabel: string;
  mode: 'create' | 'edit';
  serviceTypes: ServiceType[];
  defaultValues?: MenuPositionFormFields;
  submitting: boolean;
  formError?: string;
  onCancel: () => void;
  onSubmit: (values: MenuPositionFormValues) => Promise<void>;
}

export function MenuPositionForm({
  title,
  submitLabel,
  mode,
  serviceTypes,
  defaultValues,
  submitting,
  formError,
  onCancel,
  onSubmit,
}: MenuPositionFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MenuPositionFormFields, unknown, MenuPositionFormValues>({
    resolver: zodResolver(menuPositionFormSchema) as unknown as Resolver<
      MenuPositionFormFields,
      unknown,
      MenuPositionFormValues
    >,
    defaultValues: defaultValues ?? DEFAULT_MENU_POSITION_FORM_VALUES,
  });

  function handleFormSubmit(event: FormEvent): void {
    void handleSubmit((values) => onSubmit(values))(event);
  }

  return (
    <Modal open onClose={onCancel} title={title} className="max-w-md">
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-4" noValidate>
        {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

        <Field
          label="Código"
          required
          disabled={mode === 'edit'}
          {...register('code')}
          {...maybeError(errors.code?.message)}
        />
        <Field
          label="Nombre"
          required
          {...register('name')}
          {...maybeError(errors.name?.message)}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="menu-position-service-type" className="text-sm font-medium text-slate-900">
            Tipo de servicio
            <span aria-hidden="true" className="ml-0.5 text-red-600">
              *
            </span>
          </label>
          <select
            id="menu-position-service-type"
            {...register('serviceTypeId')}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm"
          >
            <option value="">Seleccioná un tipo de servicio</option>
            {serviceTypes.map((serviceType) => (
              <option key={serviceType.id} value={serviceType.id}>
                {serviceType.name}
              </option>
            ))}
          </select>
          {errors.serviceTypeId?.message !== undefined ? (
            <p role="alert" className="text-xs text-red-600">
              {errors.serviceTypeId.message}
            </p>
          ) : null}
        </div>

        <Field
          label="Orden de presentación"
          type="number"
          min={0}
          {...register('displayOrder')}
          {...maybeError(errors.displayOrder?.message)}
        />

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" loading={submitting}>
            {submitLabel}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}
